import React, { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Menu,
  Button,
  MenuButton,
  MenuList,
  MenuItem,
  Checkbox,
  Button as ChakraButton,
  useOutsideClick,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  Stack,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import useBreakpoint from '../../utils/useBreakPoint';

export const FiltersSection = ({
  setSelectedFilters,
  selectedUrlFilterIds,
  selectedFilters,
  handleResetFilters,
  filters = [],
}: {
  setSelectedFilters: (filterIds: string[]) => void;
  selectedFilters: string[];
  selectedUrlFilterIds?: string[];
  handleResetFilters: (groupFilters: FilterConfig[]) => void;
  filters: BuildABoxFilterGroup[];
}) => {
  const { t } = useTranslation();
  const { isBigScreen } = useBreakpoint();
  const handleResetFiltersClick = () => {
    setSelectedFilters(selectedUrlFilterIds || []);
  };
  return (
    <FiltersContainer>
      {t('babFilters.filters')}
      {filters.map(({ Id, Filters, GroupName }) =>
        isBigScreen ? (
          <Filter
            key={Id}
            label={GroupName}
            filters={Filters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            handleResetFilters={handleResetFilters}
          />
        ) : (
          <FilterMobile
            key={Id}
            label={GroupName}
            filters={Filters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            handleResetFilters={handleResetFilters}
          />
        ),
      )}
      <Button px={4} py={2} variant="link" onClick={handleResetFiltersClick}>
        {t('babFilters.reset')}
      </Button>
    </FiltersContainer>
  );
};
const FiltersContainer = styled.div.attrs(() => ({
  className: `awt-filters-container`,
}))`
  display: flex;
  gap: 16px;
  margin: 0 auto;
  isolation: isolate;
  z-index: 2;
  overflow-x: auto;
  max-width: 100%;
  align-items: baseline;
`;
const Filter = ({
  label,
  filters = [],
  setSelectedFilters,
  selectedFilters,
  handleResetFilters,
}: {
  label: string;
  filters: FilterConfig[];
  setSelectedFilters: (filterIds: string[]) => void;
  selectedFilters: string[];
  handleResetFilters: (groupFilters: FilterConfig[]) => void;
}) => {
  const selectedFiltersCount = selectedFilters.filter((selectedFilter) =>
    filters.map(({ Id }) => Id).includes(selectedFilter),
  ).length;
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
  const outside = useRef(null);
  const { t } = useTranslation();

  useOutsideClick({
    ref: outside,
    handler: () => setIsFilterMenuOpen(false),
  });

  const handleOnCheckboxChangeBase = (e, filterId) => {
    if (e.target.checked) {
      return setSelectedFilters([...selectedFilters, filterId]);
    }
    const newSelectedFilters = selectedFilters.filter(
      (selectedFiltersId) => selectedFiltersId !== filterId,
    );

    return setSelectedFilters(newSelectedFilters);
  };
  const handleOnCheckboxChange = useCallback(handleOnCheckboxChangeBase, [
    selectedFilters,
    setSelectedFilters,
  ]);

  return (
    <Menu isOpen={isFilterMenuOpen}>
      <MenuButton
        px={4}
        py={2}
        variant="link"
        as={ChakraButton}
        onClick={() => setIsFilterMenuOpen(true)}
        rightIcon={<ChevronDownIcon />}
        className="awt-filter-button"
      >
        {label} {selectedFiltersCount > 0 && `(${selectedFiltersCount})`}
      </MenuButton>
      <MenuList p={6} ref={outside} className="awt-filters-menu-list">
        <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
          <ChakraButton
            variant="link"
            onClick={() => handleResetFilters(filters)}
            className="awt-reset-filters-button"
          >
            {t('babFilters.reset')}
          </ChakraButton>
        </div>

        {filters.map(({ Name, Id }) => {
          const isFilterSelected = !!selectedFilters.find(
            (filterId) => filterId === Id,
          );

          return (
            <MenuItem
              key={Id}
              _focus={{ background: 'unset' }}
              _active={{ background: 'unset' }}
              px="6"
              minW="200px"
              className="awt-filter-menu-item"
            >
              <Checkbox
                value={Id}
                onChange={(e) => handleOnCheckboxChange(e, Id)}
                isChecked={isFilterSelected}
                size="lg"
                className="awt-filter-checkbox"
              >
                {Name}
              </Checkbox>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};
const FilterMobile = ({
  label,
  filters = [],
  setSelectedFilters,
  selectedFilters,
  handleResetFilters,
}: {
  label: string;
  filters: FilterConfig[];
  setSelectedFilters: (filterIds: string[]) => void;
  selectedFilters: string[];
  handleResetFilters: (groupFilters: FilterConfig[]) => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const btnRef = React.useRef(null);
  const { t } = useTranslation();
  const handleOnCheckboxChangeBase = (e, filterId) => {
    if (e.target.checked) {
      return setSelectedFilters([...selectedFilters, filterId]);
    }
    const newSelectedFilters = selectedFilters.filter(
      (selectedFiltersId) => selectedFiltersId !== filterId,
    );

    return setSelectedFilters(newSelectedFilters);
  };
  const handleOnCheckboxChange = useCallback(handleOnCheckboxChangeBase, [
    selectedFilters,
    setSelectedFilters,
  ]);

  return (
    <>
      <ChakraButton
        px={4}
        py={2}
        flex="0 0 auto"
        variant="link"
        onClick={onOpen}
        rightIcon={<ChevronDownIcon />}
        className="awt-filter-button"
      >
        {label}
      </ChakraButton>

      <Drawer
        isOpen={isOpen}
        placement="bottom"
        onClose={onClose}
        finalFocusRef={btnRef}
      >
        {/* some themes add the :empty pseudo with display:none for empty elements, making the overlay not displaying */}
        <DrawerOverlay>
          <div></div>
        </DrawerOverlay>
        <DrawerContent>
          <DrawerBody className="awt-filters-drawer-body">
            <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <ChakraButton
                variant="link"
                onClick={() => handleResetFilters(filters)}
                className="awt-reset-filters-button"
              >
                {t('babFilters.reset')}
              </ChakraButton>
            </div>
            <Stack spacing={5} direction="column" className="awt-filters-stack">
              {filters.map(({ Name, Id }) => {
                const isFilterSelected = !!selectedFilters.find(
                  (filterId) => filterId === Id,
                );

                return (
                  <Checkbox
                    key={Id}
                    value={Id}
                    onChange={(e) => handleOnCheckboxChange(e, Id)}
                    isChecked={isFilterSelected}
                    size="lg"
                    className="awt-filter-checkbox"
                  >
                    {Name}
                  </Checkbox>
                );
              })}
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
